type MessageListener = (data: string) => void;

/**
 * Singleton WebSocket service for managing shipment-related WebSocket connections
 */
class ShipmentWebSocketService {
  private socket: WebSocket | null;
  public url: string | null;
  private messageListeners: MessageListener[];

  constructor() {
    this.socket = null;
    this.url = null;
    this.messageListeners = [];
  }

  /**
   * Connects to a WebSocket URL, disconnecting any existing connection first
   *
   * @param url - The WebSocket URL to connect to
   */
  connect(url: string): void {
    if (this.socket) {
      this.disconnect();
    }

    this.url = url;
    console.log('🚀 Connecting to Shipment WebSocket:', url);
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('✅ Shipment WebSocket Connected:', url);
    };

    this.socket.onerror = (error: Event) => {
      console.error('🚨 Shipment WebSocket Error:', error);
    };

    this.socket.onclose = () => {
      this.socket = null;
      console.log('🔌 Shipment WebSocket Disconnected.');
    };

    this.socket.onmessage = (event: MessageEvent) => {
      console.log('📩 Shipment WebSocket Message:', event.data);
      this.notifyListeners(event.data);
    };
  }

  /**
   * Disconnects the current WebSocket connection if one exists
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }

  /**
   * Sends a message through the WebSocket connection if it's open
   *
   * @param message - The message object to send (will be JSON stringified)
   */
  sendMessage(message: unknown): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('⚠ WebSocket is not connected! Cannot send message.');
    }
  }

  /**
   * Registers a callback function to receive WebSocket messages
   *
   * @param callback - Function to call when messages are received
   */
  onMessage(callback: MessageListener): void {
    if (!this.messageListeners.includes(callback)) {
      this.messageListeners.push(callback);
    }
  }

  /**
   * Removes a message listener callback
   *
   * @param callback - The callback function to remove
   */
  removeMessageListener(callback: MessageListener): void {
    this.messageListeners = this.messageListeners.filter(
      (cb) => cb !== callback
    );
  }

  /**
   * Notifies all registered listeners with received data
   *
   * @param data - The message data received from WebSocket
   */
  private notifyListeners(data: string): void {
    this.messageListeners.forEach((callback) => callback(data));
  }
}

export default new ShipmentWebSocketService();
